// 事件已看（EVT-03）：每個 plane 一份（水位線 + 水位線之後個別看過的 id；app-api /v1/me/vms/event-seen）。
// 未看 = 發生在水位線之後、而且沒被個別標過。選單的未看數 = 那段時間的警報 + 偵測（搜尋 total）扣掉個別標過的，最多顯示 99+。
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { kinioFetch, planeBase } from '@kinio/web-sdk';
import { useScope } from './scope';
import { searchApi } from './planeApi';

export interface EventSeen {
  seen_until: string | null;
  marks: string[];
}

export const eventSeenApi = {
  get: (api: string) =>
    kinioFetch<Partial<EventSeen>>(`${planeBase(api)}/v1/me/vms/event-seen`).then((r) => ({
      seen_until: typeof r?.seen_until === 'string' ? r.seen_until : null,
      marks: Array.isArray(r?.marks) ? r.marks : [],
    })),
  all: (api: string, at: string) => kinioFetch<EventSeen>(`${planeBase(api)}/v1/me/vms/event-seen`, { method: 'PUT', body: { seen_until: at } }),
  mark: (api: string, ids: string[]) => kinioFetch(`${planeBase(api)}/v1/me/vms/event-seen/marks`, { method: 'POST', body: { event_ids: ids } }),
};

/** 這一筆算不算沒看過。 */
export function isUnseen(s: EventSeen | undefined, eventId: string, occurredAt: string): boolean {
  if (!s) return false; // 還沒讀到狀態：先不標
  if (s.marks.includes(eventId)) return false;
  return !s.seen_until || occurredAt > s.seen_until;
}

/** 未看數：從沒標過的人不算（不然一進來就是整個保存期的數量）——給 0，等他第一次「全部標為已看」。 */
export function unseenQuery(s: EventSeen | undefined): string | null {
  if (!s?.seen_until) return null;
  return `after:${new Date(s.seen_until).toISOString().replace(/\.\d{3}Z$/, 'Z')} level:alarm,detection`;
}

export function useEventSeen() {
  const { planes } = useScope();
  const qc = useQueryClient();
  const qs = useQueries({
    queries: planes.map((p) => ({ queryKey: ['event-seen', p.api], queryFn: () => eventSeenApi.get(p.api), staleTime: 30_000, retry: false })),
  });
  const byApi = new Map<string, EventSeen>();
  planes.forEach((p, i) => {
    if (qs[i]?.data) byApi.set(p.api, qs[i].data!);
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['event-seen'] });
  return {
    byApi,
    mark: async (api: string, ids: string[]) => {
      const cur = byApi.get(api);
      if (!ids.length || (cur && ids.every((id) => cur.marks.includes(id)))) return;
      qc.setQueryData(['event-seen', api], (old: EventSeen | undefined) => (old ? { ...old, marks: [...new Set([...old.marks, ...ids])] } : old));
      await eventSeenApi.mark(api, ids).catch(() => undefined);
      void qc.invalidateQueries({ queryKey: ['unseen-count'] });
    },
    markAll: async () => {
      const at = new Date().toISOString();
      await Promise.all(planes.map((p) => eventSeenApi.all(p.api, at).catch(() => undefined)));
      refresh();
      void qc.invalidateQueries({ queryKey: ['unseen-count'] });
    },
  };
}

/** 選單上的未看數（每分鐘更新）。 */
export function useUnseenCount(): number | null {
  const { planes, visible } = useScope();
  const { byApi } = useEventSeen();
  const qs = useQueries({
    queries: planes.map((p) => {
      const s = byApi.get(p.api);
      const q = unseenQuery(s);
      const homes = visible.filter((h) => h.endpoints.api === p.api).map((h) => h.home_id);
      return {
        queryKey: ['unseen-count', p.api, q, homes.join(',')],
        enabled: !!q && homes.length > 0,
        refetchInterval: 60_000,
        retry: false,
        queryFn: async () => {
          const r = await searchApi.events(p.api, { q: q!, homes, limit: 1 });
          const marksAfter = s?.marks.length ?? 0;
          return Math.max(0, (r?.total ?? 0) - marksAfter);
        },
      };
    }),
  });
  if (!qs.some((q) => q.isSuccess)) return null;
  return qs.reduce((n, q) => n + (q.data ?? 0), 0);
}
